import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { IWinLevel } from "../../../abstracts/types/interfaces";
import { displayConfig, winLevels } from "../../config/ApocalypseConfig";
import { ApocalypseTotalWinScreen } from "./Screens/ApocalypseTotalWinScreen";
import { ApocalypseWaysWinScreen } from "./Screens/ApocalypseWaysWinScreen";
import { ApocalypseFreespinIntroScreen } from "./Screens/freespin/ApocalypseFreespinIntroScreen";
import { ApocalypseFreespinOutroScreen } from "./Screens/freespin/ApocalypseFreespinOutroScreen";
import { ApocalypseFreespinRestoreScreen } from "./Screens/freespin/ApocalypseFreespinRestoreScreen";
import { ApocalypseSuperFreespinOutroScreen } from "./Screens/superFreespin/ApocalypseSuperFreespinOutroScreen";

export class ApocalypseWinView extends AbstractView {
    public totalWinScreen: ApocalypseTotalWinScreen;
    public waysWinScreen: ApocalypseWaysWinScreen;

    private freespinIntroScreen: ApocalypseFreespinIntroScreen;
    private freespinRestoreScreen: ApocalypseFreespinRestoreScreen;
    private freespinOutroScreen: ApocalypseFreespinOutroScreen;
    private superFreespinOutroScreen: ApocalypseSuperFreespinOutroScreen;

    constructor() {
        super(`ApocalypseWinningsView`);
    }

    protected initGraphics() {
        super.initGraphics();

        this.totalWinScreen = new ApocalypseTotalWinScreen(this);
        this.totalWinScreen.x = displayConfig.reelsViewWidth / 2;
        this.totalWinScreen.y = displayConfig.reelsViewHeight / 2 - 50;

        this.waysWinScreen = new ApocalypseWaysWinScreen(this);
        this.waysWinScreen.x = this.totalWinScreen.x;
        this.waysWinScreen.y = this.totalWinScreen.y;

        this.freespinIntroScreen = new ApocalypseFreespinIntroScreen(this);
        this.freespinIntroScreen.x = 360;
        this.freespinIntroScreen.y = 360;

        this.freespinOutroScreen = new ApocalypseFreespinOutroScreen(this);
        this.freespinOutroScreen.x = this.freespinIntroScreen.x;
        this.freespinOutroScreen.y = this.freespinIntroScreen.y;

        this.superFreespinOutroScreen = new ApocalypseSuperFreespinOutroScreen(this);
        this.superFreespinOutroScreen.x = this.freespinOutroScreen.x;
        this.superFreespinOutroScreen.y = this.freespinOutroScreen.y;
    }

    public showFreespinIntroScreen(onClick: Function, fsLeft: number, gameMode: AbstractGameMode) {
        this.freespinIntroScreen.onContinueBtnClick = onClick;

        const timeline = gsap.timeline({
            onStart: () => {
                this.addChild(this.freespinIntroScreen);
            },
        });

        timeline.add(this.freespinIntroScreen.getGsapAnimation(fsLeft, gameMode));
    }

    public showFreespinRestoreScreen(
        onClick: Function,
        fsLeft: number,
        gameMode: AbstractGameMode
    ): gsap.core.Timeline {
        this.freespinRestoreScreen = new ApocalypseFreespinRestoreScreen(this);
        this.freespinRestoreScreen.x = this.freespinIntroScreen.x;
        this.freespinRestoreScreen.y = this.freespinIntroScreen.y;
        this.freespinRestoreScreen.onContinueBtnClick = onClick;

        const timeline = gsap.timeline({
            onStart: () => {
                this.addChild(this.freespinRestoreScreen);
            },
        });

        timeline.add(this.freespinRestoreScreen.getGsapAnimation(fsLeft, gameMode));

        return timeline;
    }

    public showFreespinOutroScreen(
        totalWin: number,
        onClick: Function,
        isSuperFs: boolean = false,
        winLevel: IWinLevel = winLevels[0],
        itShouldPlaySound: boolean = true,
        controller: FlowStateController
    ): gsap.core.Timeline {
        const outroScreen = isSuperFs ? this.superFreespinOutroScreen : this.freespinOutroScreen;
        const timeline = gsap.timeline({
            onStart: () => {
                this.addChild(outroScreen);
            },
            onComplete: () => {
                controller?.setSkipCallback(undefined);
            },
        });

        outroScreen.onContinueBtnClick = onClick;

        timeline.addLabel(`outroScreenStart`, 0);
        timeline.add(
            outroScreen.getGsapAnimation(totalWin, winLevel, itShouldPlaySound),
            `outroScreenStart`
        );
        timeline.addLabel(`outroScreenComplete`, `>-=0.2`);

        return timeline;
    }

    public openIntroScreenDoors(): gsap.core.Timeline {
        return this.freespinIntroScreen.openDoors();
    }

    public openRestoreScreenDoors(): gsap.core.Timeline {
        const tl = gsap.timeline({
            onComplete: () => {
                if (this.freespinRestoreScreen) {
                    this.removeChild(this.freespinRestoreScreen);
                    this.freespinRestoreScreen.destroy();
                }
            },
        });

        tl.add(this.freespinRestoreScreen.openDoors());

        return tl;
    }

    public openOutroScreenDoors(isSuperFs: boolean): gsap.core.Timeline {
        return isSuperFs
            ? this.superFreespinOutroScreen.openDoors()
            : this.freespinOutroScreen.openDoors();
    }

    public override onResize(): void {
        super.onResize();

        this.freespinIntroScreen.onOrientationChange();
        // ! to be checked once outro screen is implemented
        this.freespinOutroScreen.onOrientationChange();
        this.freespinRestoreScreen?.onOrientationChange();
    }
}
